import { useModal } from '@ebay/nice-modal-react'
import { useEffect, useState } from 'react'
import { isIOS, isMobile } from 'react-device-detect'

import { countryIncludes } from '../../constants/countries'
import useAppDownload from '../../hooks/useAppDownload'
import { isPWA } from '../../utils/device'
import { getBrowserName } from '../../utils/getBrowserName'
import Icon from '../icon'
import PwaDownloadStepsModal from './pwa-download-steps-modal'
import PWATutorial from './pwa-tutorial'

const STORAGE_KEY = 'download-app-dismissed'

const pwa_tutorial_map: Record<string, () => Promise<{ default: string }>> = {
  chrome: () => import('../../assets/pwa-video-tutorial/pwa_chrome_brave.mp4'),
  brave: () => import('../../assets/pwa-video-tutorial/pwa_chrome_brave.mp4'),
  firefox: () => import('../../assets/pwa-video-tutorial/pwa_firefox.mp4'),
  opera: () => import('../../assets/pwa-video-tutorial/pwa_opera.mp4'),
  samsung: () => import('../../assets/pwa-video-tutorial/pwa_samsung.mp4'),
}

const DownloadAppModal = () => {
  const [isVisible, setIsVisible] = useState(false)
  const downloadLinks = useAppDownload()
  const pwaTutorial = useModal(PWATutorial)
  const pwaSteps = useModal(PwaDownloadStepsModal)

  useEffect(() => {
    const hideAndroidDownload =
      !isIOS && isMobile && countryIncludes(['PY'])
    if (
      !isMobile ||
      isPWA() ||
      window.ReactNativeWebView ||
      window?.flutter_inappwebview?.callHandler ||
      window?.jsBridge ||
      hideAndroidDownload
    )
      return

    const dismissed = localStorage.getItem(STORAGE_KEY)
    if (dismissed) {
      const dismissedDate = new Date(dismissed).toDateString()
      const today = new Date().toDateString()
      if (dismissedDate === today) return
    }

    setIsVisible(true)
  }, [])

  const dismiss = () => {
    setIsVisible(false)
    localStorage.setItem(STORAGE_KEY, new Date().toISOString())
  }

  if (!isVisible) return null

  const handleDownload = async () => {
    dismiss()

    if (downloadLinks.android.download_link) {
      window.open(downloadLinks.android.download_link, '_blank')
    } else {
      const tutorial = await pwa_tutorial_map[getBrowserName()]()
      pwaTutorial.show({ pwa_tutorial: tutorial.default })
    }
  }

  const handleIosDownload = () => {
    dismiss()
    pwaSteps.show({
      onConfirm: () =>
        localStorage.setItem(STORAGE_KEY, new Date().toISOString()),
    })
  }

  return (
    <div className="fixed inset-0 z-[300]">
      <div
        className="absolute inset-0 bg-black/50 animate-fadeIn"
        onClick={dismiss}
      />
      <div className="absolute bottom-20 left-0 right-0 animate-slideUp">
        <div className="relative mx-3 max-w-[500px] rounded-2xl px-6 pt-5 pb-5 border-solid border-2 border-[#c8a249] bg-primary dark:bg-primary-bg">
          <button
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/10"
            onClick={dismiss}>
            <Icon id="close" width="12" height="12" fill="#999" />
          </button>

          <h3 className="text-gold-txt text-xl font-bold text-center">
            Descarga la app
          </h3>

          {isIOS ? (
            <>
              <p className="text-txt text-sm text-center mt-1">
                para retiro instantáneo{' '}
                <span className="text-gold-txt font-semibold">
                  más rápido y seguro
                </span>
              </p>

              <div className="flex items-center justify-center gap-6 mt-4">
                <div className="flex items-center gap-1.5">
                  <span className="text-base">🚀</span>
                  <span className="text-txt text-sm">
                    Más rápido <span className="text-gold-txt">en la app</span>
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-base">✅</span>
                  <span className="text-gold-txt text-sm">Más seguro</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-3 w-full justify-center py-2 relative">
                  <span className="text-2xl">🚀</span>
                  <span className="text-txt font-semibold">
                    Retiro más rápido
                  </span>
                  <div
                    className="absolute bottom-0 left-0 right-0 h-[1px]"
                    style={{
                      background:
                        'linear-gradient(90deg, transparent 5%, #774605 20%, #f5c542 40%, #fff8e0 50%, #f5c542 60%, #774605 80%, transparent 95%)',
                    }}
                  />
                </div>
                <div className="flex items-center gap-3 w-full justify-center py-2 relative">
                  <span className="text-2xl">🛡️</span>
                  <span className="text-txt font-semibold">
                    Descarga segura y verificada
                  </span>
                  <div
                    className="absolute bottom-0 left-0 right-0 h-[1px]"
                    style={{
                      background:
                        'linear-gradient(90deg, transparent 5%, #774605 20%, #f5c542 40%, #fff8e0 50%, #f5c542 60%, #774605 80%, transparent 95%)',
                    }}
                  />
                </div>
              </div>

              <p className="flex items-center justify-center gap-2 text-secondary-txt text-xs mt-3">
                <span className="text-base">💡</span>
                Android puede mostrar advertencia, es normal
              </p>
            </>
          )}

          <button
            className="w-full mt-4 py-3.5 rounded-xl text-base font-bold uppercase text-white tracking-wider border-solid border-2 border-[#c8a249]"
            style={{
              background:
                'linear-gradient(180deg, #4caf50 0%, #2e7d32 50%, #1b5e20 100%)',
              boxShadow:
                '0 0 12px rgba(200,162,73,0.5), 0 0 24px rgba(200,162,73,0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
            }}
            onClick={isIOS ? handleIosDownload : handleDownload}>
            DESCARGAR AHORA
          </button>
        </div>
      </div>
    </div>
  )
}

export default DownloadAppModal
